import User from "../types/user";
import { apiServer, apiServer_v2, clientVersion } from "../config/config";
import { fetchWithToken } from "../apis/fetchWithToken";

export interface RegistrationArgs {
  name: string;
  email: string;
  password: string;
  password_confirmation: string;
  t_and_c: string;
  gdpr: string;
  socialUser_authToken: string;
  socialUser_provider: string;
  brand_invitation_code: string;
}

export interface SocialRegistrationArgs {
  provider: string;
  id: string;
  name: string;
  email: string;
  idToken: string;
  serverAuthCode?: string;
}

export interface SyncApiResponse {
  status: string;
  message: string;
  data: User;
}

export interface SocialRegistrationDesktopArgs {
  provider: string;
  authToken: string;
}

export async function syncUser(token: string) {
  const formData = new FormData();
  formData.append("new_map", "1");
  formData.append("client_version", clientVersion);
  const response = await fetchWithToken(apiServer + "/user/sync-user", {
    method: "POST",
    body: formData,
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  const data = await response?.json();
  if (data.status === "error") {
    // Still an Error with the same .message; the code lets a portal tell an
    // MFA_REQUIRED 401 apart from a transport failure.
    throw new LoginError(data.message, data.code, data.data);
  }
  return data as SyncApiResponse;
}

export const PASSWORD_CHANGE_REQUIRED = "PASSWORD_CHANGE_REQUIRED";

/** An enrolled user must enter a code (or backup code) to finish signing in. */
export const MFA_REQUIRED = "MFA_REQUIRED";

/** An unenrolled user must set up an authenticator to finish signing in. */
export const MFA_ENROLMENT_REQUIRED = "MFA_ENROLMENT_REQUIRED";

export type MfaMethod = "totp" | "backup_code";

/**
 * `data` of an MFA_REQUIRED / MFA_ENROLMENT_REQUIRED login. The pending token
 * is the only credential the code step needs; it expires after `expires_in`
 * seconds and is bound to the portal that issued it.
 */
export interface MfaChallengeData {
  pending_token: string;
  email: string;
  expires_in: number;
  methods?: MfaMethod[];
  enforced?: boolean;
  days_remaining?: number;
}

export interface PasswordChangeRequiredData {
  email: string;
  token: string;
  min_length: number;
}

/**
 * The three shapes of failed-login lockout the backend can answer with: a
 * progressive wait, a hard account lock, and the per-IP fallback for emails
 * with no account. Clients treat all three the same, keyed on
 * `retry_after_seconds`.
 */
export const LOGIN_LOCK_CODES = [
  "ACCOUNT_LOCKED",
  "LOGIN_DELAYED",
  "TOO_MANY_ATTEMPTS",
] as const;

export type LoginLockCode = (typeof LOGIN_LOCK_CODES)[number];

export interface LoginLockData {
  retry_after_seconds: number;
  retry_at?: string;
  reason?: string;
  /** Only on the check-email-exist refusal, which still advances the client. */
  first_name?: string | null;
}

/** The password was right but the email is not yet verified (DEV-602). */
export const EMAIL_UNVERIFIED = "email_unverified";

/** How new accounts prove their email: today's link, or a 6-digit code typed into the app. */
export type VerificationMethod = "link" | "code";

/**
 * `data` of an `email_unverified` login. The backend has already re-issued the
 * credential (unless `resent` is false, inside the cooldown); `resend_available_in`
 * is the seconds until *Resend* is allowed again.
 */
export interface EmailUnverifiedData {
  verification_method: VerificationMethod;
  email?: string;
  resent?: boolean;
  resend_available_in?: number;
}

export type LoginErrorData = Partial<PasswordChangeRequiredData> &
  Partial<LoginLockData> &
  Partial<MfaChallengeData> &
  Partial<EmailUnverifiedData>;

/**
 * A rejected login. `.message` is byte-identical to what the old
 * `new Error(data.message)` threw, so existing callers are unaffected; the
 * extra fields let a caller recognise the password-policy gate or a lockout.
 */
export class LoginError extends Error {
  constructor(
    message: string,
    public code?: string,
    public data?: LoginErrorData,
  ) {
    super(message);
    this.name = "LoginError";
    // Restores the prototype chain under ES5 downlevelling, without which
    // `instanceof LoginError` is false.
    Object.setPrototypeOf(this, LoginError.prototype);
  }
}

export function isPasswordChangeRequiredError(
  err: unknown,
): err is LoginError & { data: PasswordChangeRequiredData } {
  return (
    err instanceof LoginError &&
    err.code === PASSWORD_CHANGE_REQUIRED &&
    typeof err.data?.token === "string" &&
    err.data.token.length > 0
  );
}

export function isLoginLockedError(
  err: unknown,
): err is LoginError & { code: LoginLockCode; data: LoginLockData } {
  return (
    err instanceof LoginError &&
    LOGIN_LOCK_CODES.includes(err.code as LoginLockCode) &&
    typeof err.data?.retry_after_seconds === "number" &&
    err.data.retry_after_seconds > 0
  );
}

function hasPendingToken(
  err: unknown,
  code: string,
): err is LoginError & { code: string; data: MfaChallengeData } {
  return (
    err instanceof LoginError &&
    err.code === code &&
    typeof err.data?.pending_token === "string" &&
    err.data.pending_token.length > 0
  );
}

export function isMfaRequiredError(
  err: unknown,
): err is LoginError & { code: typeof MFA_REQUIRED; data: MfaChallengeData } {
  return hasPendingToken(err, MFA_REQUIRED);
}

export function isMfaEnrolmentRequiredError(
  err: unknown,
): err is LoginError & {
  code: typeof MFA_ENROLMENT_REQUIRED;
  data: MfaChallengeData;
} {
  return hasPendingToken(err, MFA_ENROLMENT_REQUIRED);
}

export function isEmailUnverifiedError(
  err: unknown,
): err is LoginError & {
  code: typeof EMAIL_UNVERIFIED;
  data: EmailUnverifiedData;
} {
  return (
    err instanceof LoginError &&
    err.code === EMAIL_UNVERIFIED &&
    (err.data?.verification_method === "link" ||
      err.data?.verification_method === "code")
  );
}

export async function performLogin({
  email,
  password,
}: {
  email: string;
  password: string;
}) {
  const formData = new FormData();
  formData.append("email", email);
  formData.append("password", password);
  formData.append("client_version", clientVersion);
  const response = await fetch(apiServer + "/user/login", {
    method: "POST",
    body: formData,
  });
  const data = await response.json();
  if (data.status === "error") {
    throw new LoginError(data.message, data.code, data.data);
  }
  return data;
}

export async function forgotPwd(
  email: string,
  mode?: "business" | "admin" | "rn",
) {
  const formData = new FormData();
  formData.append("email", email);
  formData.append("mode", mode ?? "rn");
  const response = await fetch(apiServer + "/user/request-password", {
    method: "POST",
    body: formData,
  });
  if (response.status === 403) {
    throw new Error("Try again after few minutes");
  }
  const data = await response.json();
  if (data.status === "error") {
    throw new Error(data.message);
  }
  return { status: true, data };
}

export async function register(args: RegistrationArgs) {
  const formData = new FormData();
  formData.append("name", args.name);
  formData.append("email", args.email);
  formData.append("password", args.password);
  formData.append("password_confirmation", args.password_confirmation);
  formData.append("t_and_c", args.t_and_c);
  formData.append("gdpr", args.gdpr);
  formData.append("socialUser_authToken", "");
  formData.append("socialUser_provider", "");
  formData.append("brand_invitation_code", "");

  const response = await fetch(apiServer + "/user/register", {
    method: "POST",
    body: formData,
  });

  const data = await response.json();
  if (data.status === "error") {
    throw new Error(data.message);
  }
  const verificationMethod: VerificationMethod =
    data.data.verification_method === "code" ? "code" : "link";

  return {
    email: args.email,
    name: args.name,
    userId: data.data.user_id,
    t_and_c_accept: data.data.t_and_c_accept,
    // Additive (DEV-602): an older backend sends neither, which reads as link mode.
    verificationMethod,
    resendAvailableIn: Number(data.data.resend_available_in ?? 0),
  };
}

export async function registerBusinessUser({
  email,
  name,
  password,
  password_confirmation,
}: {
  email: string;
  name: string;
  password: string;
  password_confirmation: string;
}) {
  const formData = new FormData();
  formData.append("email", email);
  formData.append("name", name);
  formData.append("password", password);
  formData.append("password_confirmation", password_confirmation);
  const response = await fetch(apiServer + "/user/business/register", {
    method: "POST",
    body: formData,
  });
  const data = await response.json();
  if (data.status === "error") {
    throw new Error(data.message);
  }
  return data as {
    status: string;
    message: string;
    data: {
      id: number;
      email: string;
      name: string;
      access_token: string;
      refresh_token: string;
      token_type: string;
      expires_in: number;
    };
  };
}

export async function registerSocial(args: SocialRegistrationArgs) {
  const formData = new FormData();
  formData.append("provider", args.provider);
  formData.append("id", args.id);
  formData.append("name", args.name);
  formData.append("email", args.email);
  formData.append("idToken", args.idToken);
  formData.append("client_version", clientVersion);
  if (args.serverAuthCode) {
    formData.append("serverAuthCode", args.serverAuthCode);
  }
  const response = await fetch(apiServer_v2 + "/user/social/login", {
    method: "POST",
    body: formData,
  });
  // Guard against non-JSON responses (e.g. a 500 HTML error page) so callers get
  // an actionable status instead of a cryptic "JSON parse error".
  const raw = await response.text();
  let data;
  try {
    data = JSON.parse(raw);
  } catch {
    throw new Error(
      `Social login failed: server returned ${response.status} (${response.statusText}) with a non-JSON response: ${raw.slice(0, 200)}`,
    );
  }
  if (data.status === "error") {
    throw new Error(data.message);
  }
  return data;
}

export async function registerSocialDesktop(
  args: SocialRegistrationDesktopArgs,
) {
  const response = await fetch(apiServer_v2 + "/user/desktop-social/login", {
    method: "POST",
    body: JSON.stringify({
      provider: args.provider,
      authToken: args.authToken,
    }),
    headers: {
      "Content-Type": "application/json",
    },
  });
  const data = await response.json();
  if (data.status === "error") {
    throw new Error(data.message);
  }
  return data;
}

export async function deleteAccount(userId: number, accessToken: string) {
  const formData = new FormData();
  formData.append("id", userId.toString());
  console.log(formData);
  const response = await fetchWithToken(apiServer + `/user/profile-delete`, {
    method: "POST",
    body: formData,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });
  const data = await response?.json();
  if (data.status === "error") {
    throw new Error(data.message);
  }
  return data;
}

export async function updatePassword(
  currentPassword: string,
  newPassword: string,
  confirmPassword: string,
  accessToken: string,
) {
  const formData = new FormData();
  formData.append("current_password", currentPassword);
  formData.append("password", newPassword);
  formData.append("password_confirmation", confirmPassword);
  console.log(formData);
  const response = await fetchWithToken(
    apiServer + "/user/check-update-profile-user-password",
    {
      method: "POST",
      body: formData,
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );
  const data = await response?.json();
  if (data.status === "error") {
    if (data.errors) {
      if (data.errors["password"]) {
        throw new Error(data.errors["password"][0]);
      }
    }
    throw new Error(data.message);
  }
  return data;
}

export async function resetPassword(
  email: string,
  code: string,
  password: string,
  passwordConfirmation: string,
  mode?: "business" | "admin",
) {
  const formData = new FormData();
  formData.append("email", email);
  formData.append("token", code);
  formData.append("password", password);
  formData.append("password_confirmation", passwordConfirmation);
  if (mode) {
    formData.append("mode", mode);
  }
  const response = await fetch(apiServer + "/user/reset-password", {
    method: "POST",
    body: formData,
  });
  const data = await response.json();
  if (data.status === "error") {
    if (data.errors) {
      if (data.errors["password"]) {
        throw new Error(data.errors["password"][0]);
      }
      const combinedMessage = data.errors
        ? `${data.errors.join(" ")} ${data.message}`
        : data.message;
      throw new Error(combinedMessage);
    }
    throw new Error("Unknown Error");
  }
  return data;
}

export async function setTCAgreed(token: string) {
  const formData = new FormData();
  formData.append("t_and_c_accept", "Yes");
  const response = await fetchWithToken(
    apiServer + "/user/profile-user-tandc",
    {
      method: "POST",
      body: formData,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  const data = await response?.json();
  if (data.status === "error") {
    throw new Error(data.message);
  }
  console.log(data);
  return data;
}

export async function generateOTP(email: string) {
  const formData = new FormData();
  formData.append("email", email);
  const response = await fetch(apiServer + "/user/business/send-otp", {
    method: "POST",
    body: formData,
  });
  const data = await response.json();
  if (data.status === "error") {
    throw new Error(data.message);
  }
  return data as { status: string; message: string };
}

export async function verifyOTP(email: string, code: string) {
  const formData = new FormData();
  formData.append("email", email);
  formData.append("otp", code);
  const response = await fetch(apiServer + "/user/business/verify-otp", {
    method: "POST",
    body: formData,
  });
  const data = await response.json();
  if (data.status === "error") {
    throw new Error(data.message);
  }
  return data as { status: string; message: string };
}

export async function checkBusinessInviteToken(token: string, email: string) {
  const formData = new FormData();
  formData.append("token", token);
  formData.append("email", email);
  const response = await fetch(apiServer + "/user/check-reset-token", {
    method: "POST",
    body: formData,
  });
  const data = await response.json();
  if (data.status === "error") {
    throw new Error(data.message);
  }
  return data as {
    status: string;
    valid: boolean;
    message: string;
    expires_at?: string;
    hours_remaining?: number;
    minutes_remaining?: number;
    expired_at?: string;
  };
}
